import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Search, Filter, X, Download, Calendar, Globe, DollarSign, SlidersHorizontal } from 'lucide-react';
import type { ShopifyOrder } from '../../types/shopify';
import { FINANCIAL_STATUS_OPTIONS, FULFILLMENT_STATUS_OPTIONS, formatCurrency } from '../../types/shopify';

interface OrderFiltersProps {
  orders: ShopifyOrder[];
  onFilterChange: (filteredOrders: ShopifyOrder[]) => void;
  onSearchAndFilterChange?: (search: string, financialStatus: string, fulfillmentStatus: string) => void;
}

const OrderFilters: React.FC<OrderFiltersProps> = ({ orders, onFilterChange, onSearchAndFilterChange }) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [financialStatus, setFinancialStatus] = useState('all');
  const [fulfillmentStatus, setFulfillmentStatus] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({ from: '', to: '' });
  const [country, setCountry] = useState('all');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [exporting, setExporting] = useState(false);

  // Debounce search input (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const uniqueCountries = useMemo(() => {
    const countries = new Set<string>();
    orders.forEach(order => {
      if (order.shipping_address?.country) {
        countries.add(order.shipping_address.country);
      }
    });
    return Array.from(countries).sort();
  }, [orders]);

  const searchFilterMounted = useRef(false);
  const onSearchAndFilterChangeRef = useRef(onSearchAndFilterChange);
  onSearchAndFilterChangeRef.current = onSearchAndFilterChange;

  // Trigger fetch only when user changes search/status (not when callback identity changes). Prevents loop.
  useEffect(() => {
    const cb = onSearchAndFilterChangeRef.current;
    if (!cb) return;
    if (!searchFilterMounted.current) {
      searchFilterMounted.current = true;
      return;
    }
    cb(
      debouncedSearch,
      financialStatus !== 'all' ? financialStatus : '',
      fulfillmentStatus !== 'all' ? fulfillmentStatus : ''
    );
  }, [debouncedSearch, financialStatus, fulfillmentStatus]);

  const applyFilters = useCallback(() => {
    // Apply client-side filters for date range, country, and amount (these are not in backend yet)
    let filtered = [...orders];

    // Note: Search and status filters are now handled by database query
    // Only apply if onSearchAndFilterChange is not available (fallback)
    if (!onSearchAndFilterChange) {
      if (debouncedSearch) {
        const searchLower = debouncedSearch.toLowerCase();
        filtered = filtered.filter(
          (order) =>
            order.order_number.toLowerCase().includes(searchLower) ||
            order.customer_name?.toLowerCase().includes(searchLower) ||
            order.customer_email?.toLowerCase().includes(searchLower) ||
            order.tracking_code?.toLowerCase().includes(searchLower)
        );
      }

      if (financialStatus !== 'all') {
        filtered = filtered.filter((order) => order.financial_status === financialStatus);
      }

      if (fulfillmentStatus !== 'all') {
        filtered = filtered.filter((order) => {
          if (fulfillmentStatus === 'UNFULFILLED') {
            return !order.fulfillment_status || order.fulfillment_status === 'UNFULFILLED';
          }
          return order.fulfillment_status === fulfillmentStatus;
        });
      }
    }

    if (dateRange.from) {
      const fromDate = new Date(dateRange.from);
      filtered = filtered.filter(order => new Date(order.order_date) >= fromDate);
    }

    if (dateRange.to) {
      const toDate = new Date(dateRange.to);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(order => new Date(order.order_date) <= toDate);
    }

    if (country !== 'all') {
      filtered = filtered.filter(order => order.shipping_address?.country === country);
    }

    if (minAmount) {
      const min = parseFloat(minAmount);
      if (!isNaN(min)) {
        filtered = filtered.filter(order => order.total_price >= min);
      }
    }

    if (maxAmount) {
      const max = parseFloat(maxAmount);
      if (!isNaN(max)) {
        filtered = filtered.filter(order => order.total_price <= max);
      }
    }

    onFilterChange(filtered);
  }, [orders, debouncedSearch, financialStatus, fulfillmentStatus, dateRange, country, minAmount, maxAmount, onFilterChange, onSearchAndFilterChange]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const resetFilters = () => {
    setSearch('');
    setFinancialStatus('all');
    setFulfillmentStatus('all');
    setDateRange({ from: '', to: '' });
    setCountry('all');
    setMinAmount('');
    setMaxAmount('');
  };

  const activeFilterCount = [
    search ? 1 : 0,
    financialStatus !== 'all' ? 1 : 0,
    fulfillmentStatus !== 'all' ? 1 : 0,
    dateRange.from || dateRange.to ? 1 : 0,
    country !== 'all' ? 1 : 0,
    minAmount || maxAmount ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const hasActiveFilters = activeFilterCount > 0;
  const hasPageScopeFilters = !!(dateRange.from || dateRange.to || country !== 'all' || minAmount || maxAmount);

  const exportToCSV = async () => {
    setExporting(true);
    try {
      let dataToExport = [...orders];

      if (hasActiveFilters) {
        applyFilters();
      }

      const headers = [
        'Order Number',
        'Order Date',
        'Customer Name',
        'Customer Email',
        'Total',
        'Currency',
        'Financial Status',
        'Fulfillment Status',
        'Items',
        'Shipping City',
        'Shipping Country',
        'Tracking Code',
        'Carrier',
      ];

      const rows = dataToExport.map(order => [
        order.order_number,
        new Date(order.order_date).toISOString(),
        order.customer_name || '',
        order.customer_email || '',
        order.total_price.toFixed(2),
        order.currency,
        order.financial_status,
        order.fulfillment_status || 'UNFULFILLED',
        order.line_items.map(item => `${item.quantity}x ${item.title}`).join('; '),
        order.shipping_address?.city || '',
        order.shipping_address?.country || '',
        order.tracking_code || '',
        order.carrier || '',
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `shopify-orders-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="card p-4 mb-6">
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by order number, customer, email, tracking, or product name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400"
            />
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2.5 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              hasActiveFilters
                ? 'bg-cyan-600 text-white hover:bg-cyan-700'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filters
            {hasActiveFilters && (
              <span className="bg-white dark:bg-gray-800 text-cyan-600 dark:text-cyan-400 rounded-full px-2 py-0.5 text-xs font-bold">
                {activeFilterCount}
              </span>
            )}
          </button>

          <button
            onClick={exportToCSV}
            disabled={exporting || orders.length === 0}
            className="px-4 py-2.5 rounded-lg font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <Download className={`h-4 w-4 ${exporting ? 'animate-bounce' : ''}`} />
            Export CSV
          </button>

          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="px-4 py-2.5 rounded-lg font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Reset
            </button>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-2">
                Payment Status
              </label>
              <select
                value={financialStatus}
                onChange={(e) => setFinancialStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 text-sm"
              >
                {FINANCIAL_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-2">
                Fulfillment Status
              </label>
              <select
                value={fulfillmentStatus}
                onChange={(e) => setFulfillmentStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 text-sm"
              >
                {FULFILLMENT_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-2">
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Date From
                </div>
              </label>
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-2">
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Date To
                </div>
              </label>
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-2">
                <div className="flex items-center gap-1">
                  <Globe className="h-3 w-3" />
                  Country
                </div>
              </label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 text-sm"
              >
                <option value="all">All Countries</option>
                {uniqueCountries.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-2">
                <div className="flex items-center gap-1">
                  <DollarSign className="h-3 w-3" />
                  Amount Range
                </div>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={minAmount}
                  onChange={(e) => setMinAmount(e.target.value)}
                  className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 text-sm"
                />
                <span className="text-gray-400">-</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={maxAmount}
                  onChange={(e) => setMaxAmount(e.target.value)}
                  className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 text-sm"
                />
              </div>
            </div>
          </div>
          {hasPageScopeFilters && (
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Date, country, and amount apply to the current page.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderFilters;
